import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const services = [
  {
    id: 'channel-letters',
    title: 'Channel Letter Signs',
    desc: 'Custom 3D illuminated letters engineered for maximum brand visibility day and night.',
    tag: 'Most Popular',
    img: 'https://public.readdy.ai/ai/img_res/b37cb307bef2074e039119e9ace211da.jpg',
    large: true,
  },
  {
    id: 'vehicle-wraps',
    title: 'Vehicle Wraps & Fleet Graphics',
    desc: 'Transform your fleet into rolling high-impact brand ambassadors.',
    img: 'https://capitalareasigns.com/wp-content/uploads/2025/12/Vehicle-Wraps-Square-800x800.png',
    large: false,
  },
  {
    id: 'monument-signs',
    title: 'Monument Signs',
    desc: 'Ground-level architectural signs that anchor your brand with permanence.',
    img: 'https://readdy.ai/api/search-image?query=elegant%20illuminated%20monument%20sign%20commercial%20business%20entrance%2C%20stone%20base%20with%20backlit%20LED%20lettering%2C%20manicured%20landscaping%2C%20upscale%20professional%20property%2C%20daytime%20warm%20light%20architectural%20photography&width=600&height=450&seq=cas-svc-monument-003&orientation=landscape',
    large: false,
  },
  {
    id: 'led-signs',
    title: 'LED & Illuminated Signs',
    desc: 'Energy-efficient illumination that keeps your brand working 24/7.',
    img: 'https://readdy.ai/api/search-image?query=dramatic%20night%20photograph%20vibrant%20illuminated%20LED%20business%20sign%20on%20premium%20retail%20storefront%20exterior%2C%20glowing%20neon%20warm%20colors%2C%20modern%20commercial%20building%2C%20professional%20signage%20installation%2C%20high%20impact%20visual%20branding&width=600&height=450&seq=cas-svc-led-004&orientation=landscape',
    large: false,
  },
  {
    id: 'storefront-signs',
    title: 'Storefront Signs',
    desc: 'Premium exterior signage that makes every first impression count.',
    img: 'https://readdy.ai/api/search-image?query=premium%20modern%20luxury%20retail%20storefront%20with%20professional%20backlit%20channel%20letter%20signs%20and%20frosted%20window%20graphics%2C%20clean%20minimalist%20commercial%20exterior%2C%20high-end%20business%20district%20daylight%20photography&width=900&height=420&seq=cas-svc-store-005&orientation=landscape',
    large: false,
    wide: true,
  },
  {
    id: 'interior-ada',
    title: 'Interior & ADA Signs',
    desc: 'Professional wayfinding and compliance signage that elevates your space.',
    img: 'https://readdy.ai/api/search-image?query=modern%20corporate%20office%20lobby%20brushed%20metal%20dimensional%20letters%20wall%20sign%20interior%20wayfinding%20system%2C%20clean%20minimalist%20premium%20workspace%20interior%20design%2C%20professional%20business%20branding&width=600&height=450&seq=cas-svc-interior-006&orientation=landscape',
    large: false,
  },
  {
    id: 'banners',
    title: 'Banners & Event Signage',
    desc: 'High-visibility temporary signage for openings, events, and promotions.',
    img: 'https://capitalareasigns.com/wp-content/uploads/2025/12/Custom-Banner-Solutions-Square-800x800.png',
    large: false,
  },
  {
    id: 'dtf',
    title: 'DTF & UV Transfers',
    desc: 'Precision printing for branded merchandise, apparel, and custom graphics.',
    img: 'https://capitalareasigns.com/wp-content/uploads/2026/01/DTF-Transfer-Water-Bottle-800x800.png',
    large: false,
  },
];

export default function ServicesSection() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="services" className="py-28 bg-[#F4EFE5]">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div ref={ref} className={`flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 reveal ${visible ? 'visible' : ''}`}>
          <div>
            <p className="eyebrow mb-4">What We Build</p>
            <h2 className="text-5xl md:text-6xl font-bold text-[#111111] leading-tight max-w-xl"
              style={{ fontFamily: 'Playfair Display, serif' }}>
              Signage Built for<br />
              <em className="not-italic text-gradient-gold">Brands That Lead</em>
            </h2>
          </div>
          <div className="max-w-sm">
            <span className="gold-bar mb-4" />
            <p className="text-[#666666] text-sm leading-relaxed">
              From concept to installation — every solution is custom-engineered to your brand, location, and business objectives.
            </p>
          </div>
        </div>

        {/* Services grid — asymmetric masonry */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Large featured card */}
          <div className="lg:col-span-2 lg:row-span-2">
            <ServiceCard service={services[0]} tall />
          </div>
          {/* Row 1 — 2 medium cards */}
          <div className="lg:col-span-1"><ServiceCard service={services[1]} /></div>
          <div className="lg:col-span-1"><ServiceCard service={services[2]} /></div>
          {/* Row 2 — 2 more cards */}
          <div className="lg:col-span-1"><ServiceCard service={services[3]} /></div>
          <div className="lg:col-span-1"><ServiceCard service={services[5]} /></div>
          {/* Wide bottom card */}
          <div className="lg:col-span-2"><ServiceCard service={services[4]} wide /></div>
          {/* Final 2 */}
          <div className="lg:col-span-1"><ServiceCard service={services[6]} /></div>
          <div className="lg:col-span-1"><ServiceCard service={services[7]} /></div>
        </div>

        {/* CTA */}
        <div className="flex items-center justify-center gap-5 mt-14">
          <a href="#quote" className="btn-gold">
            Request a Custom Design
            <i className="ri-arrow-right-line" />
          </a>
          <a href="/services" className="btn-outline-gold">
            View All Services
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, tall, wide }: { service: typeof services[0]; tall?: boolean; wide?: boolean }) {
  return (
    <div className={`group relative overflow-hidden card-lift cursor-pointer ${tall ? 'h-[520px] md:h-[600px]' : wide ? 'h-[260px]' : 'h-[260px]'}`}>
      <img
        src={service.img}
        alt={`${service.title} — Capital Area Signs Austin TX`}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 transition-opacity duration-300 group-hover:from-black/95" />

      {/* Tag */}
      {service.tag && (
        <div className="absolute top-4 left-4">
          <span className="bg-[#C4962A] text-black text-xs font-bold uppercase tracking-widest px-3 py-1">
            {service.tag}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <p className="eyebrow text-[#C4962A]/80 mb-2 text-xs">{service.id.replace(/-/g, ' ')}</p>
        <h3 className={`font-bold text-white leading-tight mb-2 ${tall ? 'text-2xl' : 'text-lg'}`}
          style={{ fontFamily: 'Playfair Display, serif' }}>
          {service.title}
        </h3>
        <p className={`text-white/60 leading-relaxed mb-4 ${tall ? 'text-sm' : 'text-xs'}`}>
          {service.desc}
        </p>
        <div className="flex items-center gap-2 text-[#C4962A] text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
          <span>Learn More</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    </div>
  );
}
